import React, { memo, useEffect, useRef, useState } from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Checkbox, FormControlLabel, Radio } from '@material-ui/core';
import { v1 as uuidV1 } from 'uuid';
import MaterialSelect from '../../../../shared/components/MaterialSelect';
import {
  ellipsisBetweenText,
  getAnswerGroup,
  getTranslationAnswerByQuantity,
  removeHtml,
} from '../../../../shared/utils/smartutils';
import {
  QUESTION_TRUE,
  QUESTION_TYPE,
} from '../../../../shared/utils/constants';

const GroupedQuestions = ({ t, individual }) => {
  const currentQuestion = useRef(null);
  const [groupQuestions, setGroupQuestions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState();

  const addLabelAndValueToOptionQuestion = (item) => {
    return item
      ? {
          ...item,
          label: ellipsisBetweenText(removeHtml(item.description)),
          value: item.id,
        }
      : {};
  };

  const getOptionsSelect = () => {
    return groupQuestions.map((item) => addLabelAndValueToOptionQuestion(item));
  };

  const getLabelNumberOfAnswers = (total) => {
    return (
      <div className="mb-2">
        {`${total} ${getTranslationAnswerByQuantity(total, t)}`}
      </div>
    );
  };

  const getLabelResponsibleName = (name) => {
    return <div className="mb-2">{name}</div>;
  };

  const descriptive = () => {
    const { answeredGroup } = selectedQuestion;
    return (
      <div className="mb-3">
        {getLabelNumberOfAnswers(answeredGroup.length)}
        {answeredGroup.map((answer) => (
          <>
            {getLabelResponsibleName(answer.responsible.fullName)}
            <div className="d-flex flex-column bg-light mb-3 p-2">
              <div className="mb-2">
                {answer.answers.map((item) => (
                  <div key={uuidV1()}>{removeHtml(item.responsibleAnswer)}</div>
                ))}
              </div>
            </div>
          </>
        ))}
      </div>
    );
  };

  const selection = () => {
    const { answeredGroup } = selectedQuestion;
    return (
      <div className="mb-3">
        {getLabelNumberOfAnswers(answeredGroup.length)}
        {answeredGroup.map((answer) => (
          <>
            {getLabelResponsibleName(answer.responsible.fullName)}
            <div className="d-flex flex-column bg-light mb-3 p-2">
              <div>
                {answer.answers.map((item) => (
                  <div key={uuidV1()}>
                    <div className="d-flex ">
                      {item.selected && (
                        <FormControlLabel
                          className="mb-0"
                          control={<Checkbox disabled checked />}
                          label={removeHtml(item.description)}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        ))}
      </div>
    );
  };

  const objective = () => {
    const { answeredGroup } = selectedQuestion;
    return (
      <div className="mb-3">
        {getLabelNumberOfAnswers(answeredGroup.length)}
        {answeredGroup.map((answer) => (
          <>
            {getLabelResponsibleName(answer.responsible.fullName)}
            <div className="d-flex flex-column bg-light mb-3 p-2">
              <div>
                {answer.answers.map((item) => (
                  <div key={uuidV1()}>
                    <div className="d-flex ">
                      {item.selected && (
                        <FormControlLabel
                          className="mb-0"
                          control={<Radio disabled checked />}
                          label={removeHtml(item.description)}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        ))}
      </div>
    );
  };

  const trueOrFalse = () => {
    const converterToBool = (answered) => answered === QUESTION_TRUE;
    const { answeredGroup } = selectedQuestion;

    return (
      <div className="mb-3">
        {getLabelNumberOfAnswers(answeredGroup.length)}
        {answeredGroup.map((answer) => (
          <>
            {getLabelResponsibleName(answer.responsible.fullName)}
            <div className="d-flex flex-column bg-light mb-3 p-2">
              <div>
                {answer.answers.map((item) => (
                  <div key={uuidV1()}>
                    <div className="mb-2">{item.description}</div>
                    <div className="d-flex">
                      <FormControlLabel
                        className="mb-0"
                        control={<Radio disabled checked />}
                        label={t(
                          `answers.${converterToBool(item.responsibleAnswer)}`
                        )}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        ))}
      </div>
    );
  };

  const getAnsweredGroup = () => {
    switch (selectedQuestion.type) {
      case QUESTION_TYPE.DESCRIPTIVE:
        return <>{descriptive()}</>;
      case QUESTION_TYPE.SELECTION:
        return <>{selection()}</>;
      case QUESTION_TYPE.OBJECTIVE:
        return <>{objective()}</>;
      case QUESTION_TYPE.TRUE_FALSE:
        return <>{trueOrFalse()}</>;
      default:
        return <></>;
    }
  };

  const getObjectiveOptions = () => {
    return (
      <div className="mb-3">
        {selectedQuestion.answers.map((answer) => (
          <div>
            <FormControlLabel
              className="mb-0"
              control={<Radio disabled />}
              label={removeHtml(answer.description)}
            />
          </div>
        ))}
      </div>
    );
  };

  const getSelectionOptions = () => {
    return (
      <div className="mb-3">
        {selectedQuestion.answers.map((answer) => (
          <div>
            <FormControlLabel
              className="mb-0"
              control={<Checkbox disabled />}
              label={removeHtml(answer.description)}
            />
          </div>
        ))}
      </div>
    );
  };

  const getTrueOrFalseOptions = () => {
    return (
      <div className="mb-3">
        {selectedQuestion.answers.map((answer) => (
          <div className="d-flex flex-column mb-1">
            {removeHtml(answer.description)}
            <div className="d-flex">
              <FormControlLabel
                className="mb-0"
                control={<Radio disabled />}
                label={t('answers.true')}
              />
              <FormControlLabel
                className="mb-0"
                control={<Radio disabled />}
                label={t('answers.false')}
              />
            </div>
          </div>
        ))}
      </div>
    );
  };

  const getOptionsPreview = () => {
    switch (selectedQuestion.type) {
      case QUESTION_TYPE.OBJECTIVE:
        return <>{getObjectiveOptions()}</>;
      case QUESTION_TYPE.SELECTION:
        return <>{getSelectionOptions()}</>;
      case QUESTION_TYPE.TRUE_FALSE:
        return <>{getTrueOrFalseOptions()}</>;
      default:
        return <></>;
    }
  };

  useEffect(() => {
    const questions = individual
      .filter((item) => item.questions)
      .map((item) => ({
        questions: item.questions.map((question) => ({
          ...question,
          responsible: item.responsible,
        })),
      }))
      .reduce((acc, item) => acc.concat(item.questions), []);
    const group = getAnswerGroup(questions);
    setGroupQuestions(group);
    if (group.length) {
      setSelectedQuestion(addLabelAndValueToOptionQuestion(group[0]));
    }
  }, [individual]);

  useEffect(() => {
    currentQuestion.current = selectedQuestion;
  }, [selectedQuestion]);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="w-100 mr-3">
          {selectedQuestion && (
            <MaterialSelect
              options={getOptionsSelect()}
              handleChange={setSelectedQuestion}
              selectedValue={selectedQuestion}
            />
          )}
        </div>
      </div>
      {selectedQuestion && (
        <>
          <div className="mb-3">{removeHtml(selectedQuestion.description)}</div>
          {getOptionsPreview()}
          {getAnsweredGroup()}
        </>
      )}
    </div>
  );
};

GroupedQuestions.propTypes = {
  t: PropTypes.func.isRequired,
  individual: PropTypes.arrayOf(
    PropTypes.shape({
      responsible: PropTypes.shape({
        id: PropTypes.number,
        fullName: PropTypes.string,
      }),
      questions: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          description: PropTypes.string,
          type: PropTypes.string,
          answers: PropTypes.shape({
            description: PropTypes.string,
            id: PropTypes.number,
            correct: PropTypes.bool,
            selected: PropTypes.bool,
            trueOrFalse: PropTypes.string,
            responsibleAnswer: PropTypes.string,
          }),
        })
      ),
    })
  ).isRequired,
};

export default withTranslation('common')(memo(GroupedQuestions));
